import * as React from 'react';
import { Dropdown as BootstrapDropdown, FormGroup, FormControl } from 'react-bootstrap';
import type { WrappedFieldProps } from 'redux-form';

import { bemBlock, bemElement } from 'af-utils/bem.util';

import { Button } from '@acceligentllc/storybook';

import Label from 'af-components/LockedValue/Label';

interface OwnProps {
	onValueChange: (value: Nullable<number>) => void;
	label?: string;
	timeRoundingInterval?: TimeRoundingInterval;
	withCaret?: boolean;
	className?: string;
}

type Props = OwnProps & WrappedFieldProps;

const MAX_DROPDOWN_MENU_HEIGHT = 300 * 1.1; // 10% more

const getTime = (value: number) => {
	const hours = Math.floor(value / 60);
	const minutes = value % 60;

	return { hours, minutes };
};

export enum TimeRoundingInterval {
	ONE_MINUTE_INTERVAL = 1,
	FIFTEEN_MINUTE_INTERVAL = 15
}

interface VariablesForCalculation {
	minuteIncrementValue: number;
	minuteDecrementValue: number;
	hourIncrementValue: number;
	hourDecrementValue: number;
}

const VARIABLES_DEPENDANT_ON_ROUNDING: Record<TimeRoundingInterval, VariablesForCalculation> = {
	[TimeRoundingInterval.ONE_MINUTE_INTERVAL]: {
		minuteIncrementValue: 1,
		minuteDecrementValue: 59,
		hourIncrementValue: 1,
		hourDecrementValue: 24,
	},
	[TimeRoundingInterval.FIFTEEN_MINUTE_INTERVAL]: {
		minuteIncrementValue: 15,
		minuteDecrementValue: 45,
		hourIncrementValue: 1,
		hourDecrementValue: 24,
	},
};

const DurationPicker: React.FC<Props> = (props: Props) => {

	const {
		input,
		input: { value, onBlur, onChange },
		onValueChange,
		label,
		withCaret,
		meta: { error, touched, warning },
		timeRoundingInterval = TimeRoundingInterval.FIFTEEN_MINUTE_INTERVAL,
		className,
	} = props;

	const [minutes, setMinutes] = React.useState<number>(value ? getTime(value).minutes : 0);
	const [hours, setHours] = React.useState<number>(value ? getTime(value).hours : 0);
	const [dropup, setDropup] = React.useState<boolean>(false);
	const [isOpen, setIsOpen] = React.useState<boolean>(false);

	const _dropdownInput = React.createRef<HTMLDivElement>();
	const _dropdownToggle = React.createRef<HTMLDivElement>();

	const handleFocus = React.useCallback(() => {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(input as any).onFocus();
	}, [input]);

	const handleTimeInputFocus = React.useCallback((evt: React.FocusEvent<HTMLInputElement>) => {
		evt.currentTarget.select();
	}, []);

	const onToggle = React.useCallback((_isOpen: boolean, e?, meta?) => {
		if (_isOpen) {
			handleFocus();
		}

		if (meta?.source === 'select') {
			return;
		}

		if (_isOpen && _dropdownToggle?.current) {
			const { height: bodyHeight } = document.body.getClientRects()[0];
			const { y: dropdownTogglePositionY = 0 } = _dropdownToggle.current.getClientRects()[0] ?? {};

			const isDropup = dropdownTogglePositionY > (bodyHeight - MAX_DROPDOWN_MENU_HEIGHT);

			if (dropup !== isDropup) {
				setDropup(isDropup);
			}
		}
		setIsOpen(_isOpen);
	}, [_dropdownToggle, dropup, handleFocus]);

	const getClassName = React.useCallback(() => {
		let cn = 'dropdown-field';
		cn = className ? `${cn} ${className}` : cn;
		cn = touched && error ? `${cn} dropdown-field--error` : cn;
		return cn;
	}, [className, error, touched]);

	const renderSelected = React.useCallback(() => {
		if (!value) {
			return '0h 0m';
		}
		return `${Math.floor(value / 60)}h ${value % 60}m`;
	}, [value]);

	const getInputFormatted = React.useCallback((_value: number) => {
		if (_value < 10) {
			return `0${_value}`;
		}
		return _value;
	}, []);

	const handleHoursChange = React.useCallback(({ currentTarget: { value: hoursValue } }: React.ChangeEvent<HTMLInputElement>) => {
		let nextHours = parseInt(hoursValue);
		if (!nextHours || nextHours < 0 || nextHours > 24) {
			nextHours = 0;
		}

		setHours(nextHours);
		if (nextHours === 24) {
			setMinutes(0);
		}
	}, []);

	const handleMinutesChange = React.useCallback(({ currentTarget: { value: minutesValue } }: React.ChangeEvent<HTMLInputElement>) => {
		const _minutes = parseInt(minutesValue) % 60;
		if (hours === 24 && _minutes !== 0) {
			setHours(23);
		}
		setMinutes(_minutes);
	}, [hours]);

	const handleMinutesBlur = React.useCallback(() => {
		const _minutes = Math.round(minutes / timeRoundingInterval) * timeRoundingInterval % 60;
		if (hours === 24 && _minutes !== 0) {
			setHours(23);
		}
		setMinutes(_minutes);
	}, [hours, minutes, timeRoundingInterval]);

	const hourIncrement = React.useCallback(() => {
		const nextHours = (hours + 1) % 25;
		setHours(nextHours);
		if (nextHours === 24) {
			setMinutes(0);
		}
	}, [hours]);

	const hourDecrement = React.useCallback(() => {
		const nextHours = (hours + 24) % 25;
		setHours(nextHours);
		if (nextHours === 24) {
			setMinutes(0);
		}
	}, [hours]);

	const minuteIncrement = React.useCallback(() => {
		const minIncrement = VARIABLES_DEPENDANT_ON_ROUNDING[timeRoundingInterval].minuteIncrementValue;
		const nextMinutes = (minutes + minIncrement) % 60;
		setMinutes(nextMinutes);
		if (nextMinutes !== 0 && hours === 24) {
			setHours(23);
		}
	}, [hours, minutes, timeRoundingInterval]);

	const minuteDecrement = React.useCallback(() => {
		const minDecrement = VARIABLES_DEPENDANT_ON_ROUNDING[timeRoundingInterval].minuteDecrementValue;
		const nextMinutes = (minutes + minDecrement) % 60;
		setMinutes(nextMinutes);
		if (nextMinutes !== 0 && hours === 24) {
			setHours(23);
		}
	}, [hours, minutes, timeRoundingInterval]);

	const handleSave = React.useCallback(() => {
		const nextValueFormatted = hours * 60 + minutes;
		onValueChange?.(nextValueFormatted);
		onChange(nextValueFormatted);
		onBlur(nextValueFormatted);
		onToggle(false);
	}, [hours, minutes, onBlur, onChange, onToggle, onValueChange]);

	const handleClear = React.useCallback(() => {
		onChange(null);
		onValueChange?.(null);
		onBlur(null);
		onToggle(false);
	}, [onBlur, onChange, onToggle, onValueChange]);

	const dropdownMenuClassName = bemBlock('dropdown-menu', { 'scroll-container': true });

	const dropdownToggleClassName = bemBlock('dropdown-toggle', { 'with-caret': !!withCaret });

	return (
		<FormGroup>
			<div className="input-header" ref={_dropdownInput}>
				<Label
					label={label}
					tooltipPlacement="top"
					withMargin={true}
				/>
			</div>
			<BootstrapDropdown
				className={getClassName()}
				drop={dropup ? 'up' : undefined}
				onToggle={onToggle}
				show={isOpen}
			>
				<BootstrapDropdown.Toggle
					className={`${dropdownToggleClassName}`}
				>
					<div className="dropdown-menu__selected" ref={_dropdownToggle}>
						{renderSelected()}
					</div>
					{withCaret && <span className="icon-down"></span>}
				</BootstrapDropdown.Toggle>
				<BootstrapDropdown.Menu
					className={dropdownMenuClassName}
				>
					<div className="time-picker">
						<div className={bemElement('time-picker', 'controls')}>
							<div className={bemElement('time-picker', 'controls__time-input-container')}>
								<span
									className={`${bemElement('time-picker', 'controls__shift-control')} icon-up`}
									onClick={hourIncrement}
								/>
								<input
									className={bemElement('time-picker', 'controls__time-input')}
									maxLength={3}
									onChange={handleHoursChange}
									onFocus={handleTimeInputFocus}
									value={getInputFormatted(hours)}
								/>
								<span
									className={`${bemElement('time-picker', 'controls__shift-control')} icon-down`}
									onClick={hourDecrement}
								/>
							</div>
							<div className={bemElement('time-picker', 'controls__time-input-container')}>
								<span
									className={`${bemElement('time-picker', 'controls__shift-control')} icon-up`}
									onClick={minuteIncrement}
								/>
								<input
									className={bemElement('time-picker', 'controls__time-input')}
									maxLength={3}
									onBlur={handleMinutesBlur}
									onChange={handleMinutesChange}
									onFocus={handleTimeInputFocus}
									value={getInputFormatted(minutes)}
								/>
								<span
									className={`${bemElement('time-picker', 'controls__shift-control')} icon-down`}
									onClick={minuteDecrement}
								/>
							</div>
						</div>
						<div className={bemElement('time-picker', 'options')}>
							<Button
								label="Clear"
								onClick={handleClear}
								style="secondary"
							/>
							<Button
								label="Save"
								onClick={handleSave}
								style="primary"
							/>
						</div>
					</div>
				</BootstrapDropdown.Menu>
			</BootstrapDropdown>
			<FormControl.Feedback />
			{
				touched &&
				(
					(error && <span className="help-block"><span className="icon-info" /> {error}</span>) ||
					(warning && <span className="help-block text-orange"><span className="icon-info" /> {warning}</span>)
				)
			}
		</FormGroup >
	);

};

export default React.memo(DurationPicker);

